/**
 * OCR öncesi görsel hazırlığı (masak-kimlik-ocr.md §4): EXIF yönü (tarayıcı <img> çiziminde
 * uygular), genişlik ~1600 px'e ölçekleme, gri ton, istenirse döndürme. Faz 0 spike'ında
 * MRZ'yi en iyi okuyan biçim buydu; eşikleme (threshold) zemin dokusunu bozup sonucu kötüleştirdi.
 */
const TARGET_WIDTH = 1600;
const MAX_SIDE = 2400;

export function loadImage(source: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Görsel okunamadı.'));
    };
    img.src = url;
  });
}

/**
 * Ölçeklenmiş, döndürülmüş ve (varsayılan) gri tonlu tuval. Görsel tarayıcıdan çıkmaz.
 * keepBottom < 1 ise döndürmeden sonra yalnızca alttaki o oranda bölge tutulur ve yeniden
 * ölçeklenir — pasaportta MRZ sayfanın altında; yoğun sayfada otomatik bölütleme 2. satırı kaçırıyordu.
 */
export function prepareForOcr(
  img: HTMLImageElement,
  rotation: 0 | 90 | 180 | 270 = 0,
  grayscale = true,
  keepBottom = 1,
): HTMLCanvasElement {
  if (keepBottom < 1) {
    const full = prepareForOcr(img, rotation, false);
    const sourceTop = Math.round(full.height * (1 - keepBottom));
    const sourceHeight = full.height - sourceTop;
    const scale = TARGET_WIDTH / full.width;
    const cropped = document.createElement('canvas');
    cropped.width = TARGET_WIDTH;
    cropped.height = Math.round(sourceHeight * scale);
    const cctx = cropped.getContext('2d')!;
    cctx.drawImage(full, 0, sourceTop, full.width, sourceHeight, 0, 0, cropped.width, cropped.height);
    if (grayscale) toGrayscale(cropped);
    return cropped;
  }
  return drawScaled(img, rotation, grayscale);
}

function toGrayscale(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = image.data;
  for (let i = 0; i < d.length; i += 4) {
    const y = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
    d[i] = d[i + 1] = d[i + 2] = y;
  }
  ctx.putImageData(image, 0, 0);
}

function drawScaled(img: HTMLImageElement, rotation: 0 | 90 | 180 | 270, grayscale: boolean): HTMLCanvasElement {
  let scale = TARGET_WIDTH / img.naturalWidth;
  const longest = Math.max(img.naturalWidth, img.naturalHeight) * scale;
  if (longest > MAX_SIDE) scale *= MAX_SIDE / longest;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  const quarter = rotation === 90 || rotation === 270;
  canvas.width = quarter ? h : w;
  canvas.height = quarter ? w : h;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (grayscale) toGrayscale(canvas);
  return canvas;
}

export function isOcrSupportedFile(file?: File | null): boolean {
  return !!file && /^image\/(jpeg|png|webp|bmp)$/.test(file.type);
}
